import { useMetadata } from '@api/useMetadata'
import { useTankStrategy } from '@api/useTankStrategy'
import { defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { BulkChangeBar } from '@components/Admin/TankStrategy/BulkChangeBar'
import { BulkChangeDrawer } from '@components/Admin/TankStrategy/BulkChangeDrawer'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { addCommasToNumber, BBDTag, GraviGrid, Texto, Vertical } from '@gravitate-js/excalibrr'
import queryString from 'query-string'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useUserSettings } from '@hooks/settings'
import { useSetup } from '@api/useSetup'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'tank-strategy'

export function TankStrategy() {
  const grabDefaultFilters = () => {
    const defaultFilter = {}
    const storeNumber = queryString.parse(window.location.search).store_number
    const tankId = queryString.parse(window.location.search).tank_id
    if (storeNumber) defaultFilter.store_number = storeNumber
    if (tankId) defaultFilter.tank_id = tankId
    return defaultFilter
  }
  const { data: tankMetadata } = useMetadata('tank_strategy')
  const [filters, setFilters] = useState(grabDefaultFilters())
  const { useTankStrategyOverviewQuery } = useTankStrategy()
  const { data: tankStrategyData, isFetching, refetch } = useTankStrategyOverviewQuery(filters)
  const [selectedRows, setSelectedRows] = useState([])
  const [isBulkChangeVisible, setIsBulkChangeVisible] = useState(false)
  const gridAPIRef = useRef(null)

  const { data: setupData } = useSetup()
  const isDemandModelAvailable = setupData?.demand_model_available

  const refreshAndDeselect = () => {
    refetch().then(() => {
      gridAPIRef?.current?.deselectAll()
      gridAPIRef?.current?.redrawRows(selectedRows.map(getRowId))
    })
  }
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.site_write
  const { updateSetting } = useUserSettings()
  const columnDefs = useMemo(() => getColumnDefs(canEdit, isDemandModelAvailable), [canEdit, isDemandModelAvailable])
  const getRowId = useCallback((row) => row.data?.key, [])

  const agPropOverrides = useMemo(
    () => ({
      rowSelection: canEdit ? 'multiple' : null,
      groupSelectsFiltered: true,
      groupSelectsChildren: true,
      suppressCellFocus: true,
      rowMultiSelectWithClick: true,
      enableRangeSelection: false,
      getRowId,
    }),
    [canEdit]
  )

  return (
    <Vertical>
      <Vertical flex='1'>
        <GraviGrid
          onSelectionChanged={(props) => {
            setSelectedRows(props.api.getSelectedRows())
          }}
          externalRef={gridAPIRef}
          controlBarProps={{
            title: 'Tank Strategies',
            filters,
            setFilters,
            serverParams: tankStrategyData?.params,
          }}
          loading={isFetching}
          agPropOverrides={agPropOverrides}
          columnDefs={columnDefs}
          suppressCellFocus
          storageKey={STORAGE_KEY}
          rowData={tankStrategyData?.rows}
          onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
          {...gridViewManager(STORAGE_KEY, updateSetting)}
        />
      </Vertical>
      <Vertical flex='0' style={{ minHeight: 100 }}>
        <BulkChangeBar
          setIsBulkChangeVisible={setIsBulkChangeVisible}
          selectedRows={selectedRows}
          refetch={refreshAndDeselect}
        />
      </Vertical>
      <BulkChangeDrawer
        setIsBulkChangeVisible={setIsBulkChangeVisible}
        isBulkChangeVisible={isBulkChangeVisible}
        columnDefs={columnDefs}
        selectedRows={selectedRows}
        refreshAndDeselect={refreshAndDeselect}
        tankMetadata={tankMetadata}
        isDemandModelAvailable={isDemandModelAvailable}
      />
    </Vertical>
  )
}

const getColumnDefs = (canEdit, isDemandModelAvailable) => [
  {
    checkboxSelection: canEdit,
    headerCheckboxSelection: canEdit,
    headerCheckboxSelectionFilteredOnly: true,
    minWidth: 170,
    headerName: 'State',
    field: 'is_overridden',
    valueGetter: ({ data }) => (data?.is_overridden ? 'Overridden' : 'Default'),
    cellRenderer: ({ value }) => {
      return <div>{value === 'Overridden' && <BBDTag warning>Overridden</BBDTag>}</div>
    },
  },
  {
    width: 70,
    flex: 0,
    field: 'store_number',
    headerName: 'Site',
    comparator: (a, b, nodeA, nodeB) => {
      const compareSettings = {
        numeric: true,
        sensitivity: 'base',
      }

      return nodeA?.data?.store_number.localeCompare(nodeB?.data?.store_number, undefined, compareSettings)
    },
    cellRenderer: ({ value }) => <b>{value}</b>,
  },
  {
    field: 'tank_id',
    width: 70,
    flex: 0,
    headerName: 'Tank',
    cellRenderer: ({ value }) => <BBDTag theme2>{value}</BBDTag>,
  },
  {
    field: 'product',
    headerName: 'Product',
  },
  {
    field: 'tank_size',
    headerName: 'Max Volume',
    ...defaultNumberDef('tank_size', 0),
    valueGetter: ({ data }) => parseFloat(data?.tank_size),
  },
  {
    field: 'daily_demand',
    headerName: 'Daily Demand',
    ...defaultNumberDef('daily_demand', 0),
    valueGetter: ({ data }) => parseFloat(data?.daily_demand),
  },
  {
    field: 'inventory_strategy',
    headerName: 'Strategy',
    cellRenderer: ({ value, data }) => {
      return (
        <Texto weight='bold' appearance={data.is_overridden ? 'warning' : 'default'}>
          {value}
        </Texto>
      )
    },
  },
  {
    field: 'target_min',
    headerName: 'Target Min',
    ...defaultNumberDef('target_min', 0),
    cellRenderer: ({ value, data }) => {
      return (
        <Texto align={'right'} weight='bold' appearance={data.is_overridden ? 'warning' : 'default'}>
          {addCommasToNumber(value)}
        </Texto>
      )
    },
  },
  {
    field: 'target_max',
    headerName: 'Target Max',
    ...defaultNumberDef('target_max', 0),
    cellRenderer: ({ value, data }) => {
      return (
        <Texto align={'right'} weight='bold' appearance={data.is_overridden ? 'warning' : 'default'}>
          {addCommasToNumber(value)}
        </Texto>
      )
    },
  },
  {
    field: 'minimum_load_size',
    headerName: 'Min Load Size',
    ...defaultNumberDef('minimum_load_size', 0),
    cellRenderer: ({ value, data }) => {
      return (
        <Texto align={'right'} weight='bold' appearance={data.is_overridden ? 'warning' : 'default'}>
          {addCommasToNumber(value)}
        </Texto>
      )
    },
  },
  {
    field: 'maximum_load_size',
    headerName: 'Max Load Size',
    ...defaultNumberDef('maximum_load_size', 0),
    cellRenderer: ({ value, data }) => {
      return (
        <Texto align={'right'} weight='bold' appearance={data.is_overridden ? 'warning' : 'default'}>
          {addCommasToNumber(value)}
        </Texto>
      )
    },
  },
  {
    field: 'is_split_me',
    width: 100,
    flex: 0,
    headerName: 'Split Me',
    valueGetter: ({ data }) => (data?.is_split_me ? 'Yes' : 'No'),
    cellRenderer: ({ value }) => {
      return value ? <BBDTag>{value}</BBDTag> : null
    },
  },
  {
    headerName: 'Tags',
    field: 'load_tags',
    cellRenderer: ({ value }) =>
      value === 'biggie' ? <BBDTag warning>{value}</BBDTag> : value !== 'N/A' ? <BBDTag>{value}</BBDTag> : null,
    valueGetter: ({ data }) => {
      const tag = data?.load_tags[0]?.toLowerCase()
      return tag || 'N/A'
    },
  },
  {
    field: 'demand_model_tank_profile',
    headerName: 'Demand Model Profile',
    hide: !isDemandModelAvailable,
    suppressFiltersToolPanel: !isDemandModelAvailable,
    suppressColumnsToolPanel: !isDemandModelAvailable,
    cellRenderer: ({ value, data }) => {
      return (
        <Texto weight='bold' appearance={data.is_overridden ? 'warning' : 'default'}>
          {value}
        </Texto>
      )
    },
  },
]
